import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Insignia } from './ui/basicos';
import { Dialogo } from './ui/dialogo';
import { Campo, Selector } from './ui/formulario';
import { Cargando, useTextoDeError } from './ui/estados';

export interface Etiqueta {
  readonly id: string;
  readonly nombre: string;
  readonly activa: boolean;
  readonly terceros: number;
}

interface Contacto {
  readonly id: string;
  readonly nombre: string;
  readonly cargo: string | null;
  readonly telefono: string | null;
  readonly correo: string | null;
  readonly activo: boolean;
}

interface Nota {
  readonly id: string;
  readonly tipo: string;
  readonly texto: string;
  readonly ocurridoEn: string;
  readonly correoDelUsuario: string | null;
  readonly venceEl: string | null;
  readonly hechoEn: string | null;
}

const TIPOS = ['llamada', 'visita', 'correo', 'mensaje', 'nota'] as const;

/**
 * El encabezado de una línea de bitácora: «16/08 · Llamada · marta@…».
 *
 * Está afuera del JSX porque el lint —regla 3— rechaza literales adentro, y los separadores son
 * literales aunque no sean texto traducible. Se usa en la ficha y en el bloque de inicio, que es
 * la otra razón para que viva en un solo lugar.
 */
export function encabezadoDeNota(
  fecha: string,
  tipoTraducido: string,
  autor?: string | null,
): string {
  return [fecha, tipoTraducido, autor ?? undefined]
    .filter((parte) => parte !== undefined && parte !== null && parte !== '')
    .join(' · ');
}

/**
 * El seguimiento de un cliente: sus personas, sus etiquetas y qué pasó.
 *
 * ## Por qué las tres cosas en el mismo lugar
 *
 * Porque se usan juntas y en el mismo momento: alguien abre esto **con el teléfono en la mano**,
 * mira con quién habló la última vez, llama, y anota qué pasó. Repartirlas en tres pantallas
 * convierte una llamada en tres navegaciones.
 *
 * ## Lo que la pantalla no deja hacer, y es a propósito
 *
 * **Elegir el autor ni la fecha de una nota.** No hay campos para eso porque no existen en la
 * ruta: los pone el servidor. Es lo que hace que una bitácora se pueda creer.
 */
export function FichaDeSeguimiento({
  terceroId,
  nombre,
  abierto,
  onCerrar,
}: {
  readonly terceroId: string;
  readonly nombre: string;
  readonly abierto: boolean;
  readonly onCerrar: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState<(typeof TIPOS)[number]>('llamada');
  const [venceEl, setVenceEl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [nombreDeContacto, setNombreDeContacto] = useState('');
  const [cargo, setCargo] = useState('');
  const [telefono, setTelefono] = useState('');

  const contactos = useQuery({
    queryKey: ['contactos', terceroId],
    enabled: abierto,
    queryFn: () => pedir<Contacto[]>(`/terceros/${terceroId}/contactos`),
  });
  const notas = useQuery({
    queryKey: ['notas', terceroId],
    enabled: abierto,
    queryFn: () => pedir<Nota[]>(`/terceros/${terceroId}/notas`),
  });
  const etiquetas = useQuery({
    queryKey: ['etiquetas'],
    enabled: abierto,
    queryFn: () => pedir<Etiqueta[]>('/etiquetas'),
  });
  const anotar = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>(`/terceros/${terceroId}/notas`, {
        method: 'POST',
        cuerpo: {
          tipo,
          texto: texto.trim(),
          ...(venceEl === '' ? {} : { venceEl }),
        },
      }),
    onSuccess: async () => {
      setTexto('');
      setVenceEl('');
      setError(null);
      await cola.invalidateQueries({ queryKey: ['notas', terceroId] });
      await cola.invalidateQueries({ queryKey: ['pendientes'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const marcarHecho = useMutation({
    mutationFn: (notaId: string) => pedir<void>(`/notas/${notaId}/hecho`, { method: 'POST' }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['notas', terceroId] });
      await cola.invalidateQueries({ queryKey: ['pendientes'] });
    },
  });

  const agregarContacto = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>(`/terceros/${terceroId}/contactos`, {
        method: 'POST',
        cuerpo: {
          nombre: nombreDeContacto.trim(),
          ...(cargo.trim() === '' ? {} : { cargo: cargo.trim() }),
          ...(telefono.trim() === '' ? {} : { telefono: telefono.trim() }),
        },
      }),
    onSuccess: async () => {
      setNombreDeContacto('');
      setCargo('');
      setTelefono('');
      setError(null);
      await cola.invalidateQueries({ queryKey: ['contactos', terceroId] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const ponerEtiquetas = useMutation({
    mutationFn: (elegidas: readonly string[]) =>
      pedir<void>(`/terceros/${terceroId}/etiquetas`, {
        method: 'POST',
        cuerpo: { etiquetas: elegidas },
      }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['etiquetas-de', terceroId] });
      await cola.invalidateQueries({ queryKey: ['etiquetas'] });
    },
  });

  return (
    <Dialogo
      abierto={abierto}
      titulo={t('seguimiento.titulo')}
      descripcion={nombre}
      onCerrar={onCerrar}
    >
      <div className="flex flex-col gap-4">
        {/* Anotar va arriba de todo: es lo que se viene a hacer, y ponerlo abajo de una bitácora
            de cuarenta líneas lo esconde justo el día que la bitácora sirve. */}
        <form
          className="flex flex-col gap-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (texto.trim() !== '') anotar.mutate();
          }}
        >
          <div className="flex items-end gap-2">
            <Selector
              etiqueta={t('seguimiento.tipo')}
              value={tipo}
              onChange={(evento) => {
                setTipo(evento.target.value as (typeof TIPOS)[number]);
              }}
            >
              {TIPOS.map((clave) => (
                <option key={clave} value={clave}>
                  {t(`seguimiento.tipos.${clave}`)}
                </option>
              ))}
            </Selector>
            <div className="flex-1">
              <Campo
                etiqueta={t('seguimiento.quePaso')}
                value={texto}
                maxLength={2000}
                onChange={(evento) => {
                  setTexto(evento.target.value);
                }}
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Campo
              etiqueta={t('seguimiento.volverEl')}
              type="date"
              value={venceEl}
              ayuda={t('seguimiento.ayudaVolverEl')}
              onChange={(evento) => {
                setVenceEl(evento.target.value);
              }}
            />
            <Boton
              tono="principal"
              type="submit"
              cargando={anotar.isPending}
              disabled={texto.trim() === ''}
            >
              {t('seguimiento.anotar')}
            </Boton>
          </div>
        </form>

        {error === null ? null : (
          <p role="alert" className="text-apoyo text-mal">
            {error}
          </p>
        )}

        <section>
          <h3 className="mb-1 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
            {t('seguimiento.queSePaso')}
          </h3>
          {notas.isPending ? <Cargando /> : null}
          {notas.isSuccess && notas.data.length === 0 ? (
            <p className="text-apoyo text-texto-tenue">{t('seguimiento.sinNotas')}</p>
          ) : null}
          <ul className="flex flex-col divide-y divide-borde-tenue">
            {(notas.data ?? []).map((nota) => (
              <li key={nota.id} className="flex items-start gap-3 py-2 text-cuerpo">
                <span className="min-w-0 flex-1">
                  <span className="block text-apoyo text-texto-tenue">
                    {encabezadoDeNota(
                      nota.ocurridoEn.slice(0, 10),
                      t(`seguimiento.tipos.${nota.tipo}`, nota.tipo),
                      nota.correoDelUsuario,
                    )}
                  </span>
                  {nota.texto}
                  {nota.venceEl === null ? null : (
                    <span className="ml-2 inline-block align-middle">
                      <Insignia tono={nota.hechoEn === null ? 'atencion' : 'neutro'}>
                        {nota.hechoEn === null
                          ? t('seguimiento.venceEl', { fecha: nota.venceEl })
                          : t('seguimiento.hecho')}
                      </Insignia>
                    </span>
                  )}
                </span>
                {nota.venceEl !== null && nota.hechoEn === null ? (
                  <Boton
                    tono="sutil"
                    cargando={marcarHecho.isPending}
                    onClick={() => {
                      marcarHecho.mutate(nota.id);
                    }}
                  >
                    {t('seguimiento.marcarHecho')}
                  </Boton>
                ) : null}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h3 className="mb-1 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
            {t('seguimiento.personas')}
          </h3>
          <ul className="mb-2 flex flex-col divide-y divide-borde-tenue">
            {(contactos.data ?? []).map((contacto) => (
              <li key={contacto.id} className="py-1.5 text-cuerpo">
                {contacto.nombre}
                <span className="block text-apoyo text-texto-tenue">
                  {[contacto.cargo, contacto.telefono, contacto.correo]
                    .filter((parte) => parte !== null && parte !== '')
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
          {/* Envuelve: tres campos y un botón en una fila no entran en el ancho del diálogo, y lo
              que se sale por el borde derecho no se puede ni ver ni completar. */}
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(evento) => {
              evento.preventDefault();
              if (nombreDeContacto.trim() !== '') agregarContacto.mutate();
            }}
          >
            <Campo
              etiqueta={t('seguimiento.nombre')}
              value={nombreDeContacto}
              onChange={(evento) => {
                setNombreDeContacto(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('seguimiento.cargo')}
              value={cargo}
              onChange={(evento) => {
                setCargo(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('seguimiento.telefono')}
              value={telefono}
              onChange={(evento) => {
                setTelefono(evento.target.value);
              }}
            />
            <Boton type="submit" cargando={agregarContacto.isPending}>
              {t('acciones.agregar')}
            </Boton>
          </form>
        </section>

        <section>
          <h3 className="mb-1 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
            {t('seguimiento.etiquetas')}
          </h3>
          <EligeEtiquetas
            todas={etiquetas.data ?? []}
            terceroId={terceroId}
            onGuardar={(elegidas) => {
              ponerEtiquetas.mutate(elegidas);
            }}
          />
        </section>
      </div>
    </Dialogo>
  );
}

/**
 * Las etiquetas puestas, con casillas.
 *
 * Se leen de la fila del cliente —que ya las trae— y se guardan enteras, igual que los rubros de
 * un artículo en E19: es una lista corta que alguien mira y define completa.
 */
function EligeEtiquetas({
  todas,
  terceroId,
  onGuardar,
}: {
  readonly todas: readonly Etiqueta[];
  readonly terceroId: string;
  readonly onGuardar: (elegidas: readonly string[]) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const puestas = useQuery({
    queryKey: ['etiquetas-de', terceroId],
    queryFn: () => pedir<{ id: string }[]>(`/terceros/${terceroId}/etiquetas`),
  });
  const elegidas = (puestas.data ?? []).map((fila) => fila.id);

  if (todas.length === 0) {
    return <p className="text-apoyo text-texto-tenue">{t('seguimiento.sinEtiquetas')}</p>;
  }

  return (
    <div className="flex flex-wrap gap-3">
      {todas
        .filter((etiqueta) => etiqueta.activa || elegidas.includes(etiqueta.id))
        .map((etiqueta) => (
          <label key={etiqueta.id} className="flex items-center gap-1.5 text-cuerpo">
            <input
              type="checkbox"
              checked={elegidas.includes(etiqueta.id)}
              onChange={(evento) => {
                onGuardar(
                  evento.target.checked
                    ? [...elegidas, etiqueta.id]
                    : elegidas.filter((id) => id !== etiqueta.id),
                );
              }}
            />
            {etiqueta.nombre}
          </label>
        ))}
    </div>
  );
}
